/**
 * PaymentScheduleMarkPaidModal — mark a single milestone as paid.
 *
 * Inputs:
 *   - paidAmount  (defaults to dueAmount; warn if > dueAmount)
 *   - modeOfPayment (UPI/NEFT/RTGS/Cheque/Cash dropdown — required)
 *   - paidAt        (defaults to today, datetime-local input)
 *   - paymentRef    (UTR / cheque no — optional but recommended)
 */
import { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { formatINR } from '@/utils/formatters';
import type {
  PaymentSchedule,
  MarkPaidInput,
  ModeOfPayment,
} from '@/hooks/usePaymentSchedules';

interface Props {
  open: boolean;
  schedule: PaymentSchedule | null;
  onClose: () => void;
  onSubmit: (input: MarkPaidInput) => Promise<unknown>;
}

const MODES: { value: Exclude<ModeOfPayment, null>; label: string }[] = [
  { value: 'upi', label: 'UPI' },
  { value: 'neft', label: 'NEFT' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank transfer' },
  { value: 'wire', label: 'Wire' },
];

function nowLocalIso(): string {
  // Build a yyyy-mm-ddTHH:mm value for <input type="datetime-local">
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function PaymentScheduleMarkPaidModal({
  open,
  schedule,
  onClose,
  onSubmit,
}: Props) {
  const dueAmount =
    schedule?.dueAmount == null
      ? 0
      : typeof schedule.dueAmount === 'string'
        ? parseFloat(schedule.dueAmount) || 0
        : schedule.dueAmount;

  const [paidAmount, setPaidAmount] = useState<number>(dueAmount);
  const [modeOfPayment, setModeOfPayment] =
    useState<Exclude<ModeOfPayment, null>>('upi');
  const [paidAt, setPaidAt] = useState<string>(nowLocalIso());
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset whenever a fresh schedule is loaded into the modal
  useEffect(() => {
    if (open && schedule) {
      setPaidAmount(dueAmount);
      setModeOfPayment(
        (schedule.modeOfPayment as Exclude<ModeOfPayment, null>) || 'upi'
      );
      setPaidAt(nowLocalIso());
      setPaymentRef('');
      setSubmitError(null);
    }
  }, [open, schedule, dueAmount]);

  if (!open || !schedule) return null;

  const overPayment = paidAmount > dueAmount + 0.01;
  const underPayment = paidAmount > 0 && paidAmount < dueAmount - 0.01;
  const canSubmit = paidAmount > 0 && !!modeOfPayment && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit({
        paidAmount,
        modeOfPayment,
        paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
        paymentRef: paymentRef.trim() || undefined,
      });
      onClose();
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error || err?.message || 'Failed to mark paid'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="payment-schedule-mark-paid-modal">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Mark milestone paid</h2>
          <button
            data-testid="payment-schedule-mark-paid-modal-close-button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
          <div className="text-gray-500">
            {schedule.description || `Milestone #${schedule.sequence ?? ''}`}
          </div>
          <div className="mt-1 text-base font-semibold text-gray-900">
            Due {formatINR(dueAmount)}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Amount received (₹)
            </label>
            <input
              type="number"
              data-testid="payment-schedule-mark-paid-amount-input"
              min={0}
              step="0.01"
              value={paidAmount}
              onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            {overPayment && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                Over-payment of {formatINR(paidAmount - dueAmount)}. Ensure this is intentional.
              </p>
            )}
            {underPayment && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                Partial payment — {formatINR(dueAmount - paidAmount)} will remain unpaid.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Mode of payment
            </label>
            <select
              data-testid="payment-schedule-mark-paid-mode-select"
              value={modeOfPayment}
              onChange={(e) =>
                setModeOfPayment(e.target.value as Exclude<ModeOfPayment, null>)
              }
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            >
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Paid at
            </label>
            <input
              type="datetime-local"
              data-testid="payment-schedule-mark-paid-paid-at-input"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Payment reference (UTR / cheque no)
              <span className="ml-1 font-normal text-gray-400">— optional</span>
            </label>
            <input
              type="text"
              data-testid="payment-schedule-mark-paid-payment-ref-input"
              value={paymentRef}
              placeholder="e.g. UTR123456789012"
              onChange={(e) => setPaymentRef(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
        </div>

        {submitError && (
          <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {submitError}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-testid="payment-schedule-mark-paid-cancel-button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="payment-schedule-mark-paid-submit-button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className={
              'flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-white ' +
              (canSubmit
                ? 'bg-green-600 hover:bg-green-700'
                : 'cursor-not-allowed bg-gray-300')
            }
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Mark paid
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentScheduleMarkPaidModal;
